import { Section, sectionSpacingY } from "components/section";
import React from "react";
import { TextTitle } from "ui/text";
import { FillImage } from "components/images";
import { SimpleGrid } from "@chakra-ui/react";
import { type BlogSimple } from "lib/sanity";
import { Card } from "ui/cards";
import { cardHoverTranslation } from "utils";
import { Diptych } from "ui/diptych";
import { useCdpIndustry } from "contexts/cdp-industry-context";
import { type StaticImageData } from "next/image";
import { Button } from "ui/button";
import { blogBlurDataUrl } from "../../constants/blur-data-urls";
import redIndustryNames from "../../../public/assets/cdp-industries/industry-names/red.png";
import yellowIndustryNames from "../../../public/assets/cdp-industries/industry-names/yellow.png";
import greenIndustryNames from "../../../public/assets/cdp-industries/industry-names/green.png";
import blueIndustryNames from "../../../public/assets/cdp-industries/industry-names/blue.png";
import violetIndustryNames from "../../../public/assets/cdp-industries/industry-names/violet.png";

type CdpIndustryResourcesProps = {
  blogPosts: BlogSimple[];
};

export function CdpIndustryResources({ blogPosts }: CdpIndustryResourcesProps) {
  const { industry } = useCdpIndustry();
  let industryNames: StaticImageData;

  switch (industry.color) {
    case "red": {
      industryNames = redIndustryNames;
      break;
    }

    case "yellow": {
      industryNames = yellowIndustryNames;
      break;
    }

    case "green": {
      industryNames = greenIndustryNames;
      break;
    }

    case "blue": {
      industryNames = blueIndustryNames;
      break;
    }

    case "violet": {
      industryNames = violetIndustryNames;
      break;
    }

    // No default
  }

  return (
    <Section my="0" py={sectionSpacingY}>
      <TextTitle
        size="lg"
        align="center"
        title="Customer Data Platform resources"
        mb={{ base: "biggest", md: "huge" }}
      />

      <Diptych
        size="lg"
        direction="left"
        media={
          <FillImage
            src={industryNames}
            alt="An illustration listing names of different industries: Automotive, Consumer Subscriptions, D2C, Education, Financial Services, Healthcare, Insurance, Media & Entertainment, QSR (Food & Beverage), Retail, Technology & B2B SaaS, Telecommunications, Travel & Hospitality, and Utilities & Energy."
          />
        }
        title="Explore Customer Data Platforms by industry"
        description="Discover why leaders across all industries and adopting CDPs and how they are taking action on their customer data to drive engagement and revenue."
        actions={
          <Button hasRightChevron href="/cdp-industries">
            Explore CDP industries hub
          </Button>
        }
      />

      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3 }}
        gap="small"
        mt={{ base: "biggest", md: "huge" }}
        justifyItems="center"
      >
        {blogPosts.map((post) => (
          <Card
            key={post.slug}
            variant="shadow"
            href={`/blog/${post.slug}`}
            mediaRatio={2 / 1}
            media={<FillImage src={post.imageUrl} alt={post.imageAlt} blurDataURL={blogBlurDataUrl} />}
            title={post.title}
            description={post.excerpt}
            mb="0"
            _hover={cardHoverTranslation}
          />
        ))}
      </SimpleGrid>
    </Section>
  );
}
